import { Grid, Typography } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useState } from "react";
import { Button } from "../../shared/components/button";
import { ErrorIcon } from "../../shared/icons";
import { SuccessIcon } from "../../shared/icons/gamePlan";
import { MentorTip } from "../mentor-tip";
import { currentGamePlanIndexAtom } from "./atoms";
import { useStyles } from "./footer-actions.styles";
import { useGamePlanPath } from "./use-game-plan";

export type TipProps = {
  variant: "success" | "error";
  title: string;
  details?: string;
};

type Props = {
  backShow?: boolean;
  backDisabled?: boolean;
  backText?: string;
  backAction?: () => void;
  nextShow?: boolean;
  nextDisabled?: boolean;
  nextText?: string;
  nextAction?: () => void;
  nextPreventStep?: boolean;
  nextForceEnabled?: boolean;
  tip?: TipProps;
  className?: string;
};

export const FooterActions = ({
  backShow = true,
  backDisabled = false,
  backText = "Back",
  backAction,
  nextShow = true,
  nextDisabled = false,
  nextText = "Continue",
  nextAction,
  nextPreventStep = false,
  nextForceEnabled,
  tip,
  className,
}: Props) => {
  const classes = useStyles();
  const [isCompleting, setIsCompleting] = useState(false);
  const { quickWinIndex, isGamePlanCompleted } = useGamePlanPath();
  const [currentGamePlanIndex, setCurrentGamePlanIndex] = useAtom(
    currentGamePlanIndexAtom
  );

  const nextIndex = currentGamePlanIndex + 1;
  const gamePlanIncompleted =
    nextIndex >= quickWinIndex ? !isGamePlanCompleted : false;
  const disableNextButton = nextDisabled || isCompleting || gamePlanIncompleted;

  const backButtonAction = () => {
    if (backAction) backAction();
    setCurrentGamePlanIndex(currentGamePlanIndex - 1);
  };

  const nextButtonAction = async () => {
    if (nextAction) {
      setIsCompleting(true);
      await nextAction();
      setIsCompleting(false);
    }

    if (!nextPreventStep) {
      if (nextIndex >= quickWinIndex) {
        if (isGamePlanCompleted) {
          setCurrentGamePlanIndex(quickWinIndex);
        }
      } else {
        setCurrentGamePlanIndex(nextIndex);
      }
    }
  };

  const backButton = backShow ? (
    <Button
      data-testid="back-button"
      onClick={backButtonAction}
      variant="text"
      disabled={backDisabled}
    >
      {backText}
    </Button>
  ) : undefined;

  const nextButton = nextShow ? (
    <Button
      data-testid="next-button"
      id="wizard-next-button"
      variant={tip && (nextDisabled || isCompleting) ? "outlined" : "contained"}
      color="primary"
      loading={isCompleting}
      disabled={nextForceEnabled ? false : disableNextButton}
      endIcon={<ChevronRight />}
      onClick={nextButtonAction}
    >
      {nextText}
    </Button>
  ) : undefined;

  const title =
    tip?.variant == "success" ? (
      <Typography variant="body1" className={classes.tipTitle}>
        <SuccessIcon />
        Nice work!
      </Typography>
    ) : (
      <Grid container className="errorContainer">
        <Grid item xs={10}>
          <Typography variant="body1" className="errorTitle">
            <ErrorIcon />
            {tip?.title}
          </Typography>
        </Grid>
        {nextButton}
      </Grid>
    );

  const details =
    tip?.variant == "success" ? (
      <Grid container className="details">
        <Grid item xs={10} style={{ width: "80%" }}>
          <Typography variant="body2">{tip.details}</Typography>
        </Grid>
        <Grid item xs={2} className={classes.actionContainer}>
          {nextButton}
        </Grid>
      </Grid>
    ) : undefined;

  return (
    <>
      {!tip ? (
        <Grid item xs={12} className={clsx(classes.footerActions, className)}>
          {backButton}
          {nextButton}
        </Grid>
      ) : (
        <MentorTip variant={tip.variant} title={title} details={details} />
      )}
    </>
  );
};
